(function (scope, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (scope) scope.FereGoldTradeCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const VERSION = 1;
  const STORAGE_KEY = 'fere.goldTradeContext.v1';
  const TROY_OUNCE_GRAMS = 31.1034768;
  const CASH_FACTOR = 0.85;
  const TRADE_FACTOR = 0.90;
  const MAX_WEIGHT_GRAMS = 100000;
  const MIN_WEIGHT_GRAMS = 0.01;
  const MAX_FRESH_AGE_MS = 10 * 60 * 1000;
  const MAX_QUOTE_AGE_MS = 72 * 60 * 60 * 1000;
  const MAX_CONTEXT_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const VALID_SPOT_RANGE_USD = [500, 10000];
  const KARATS = [24, 22, 18, 14, 10];
  const ADDITIONAL_BUDGETS = [
    '',
    'Up to $500',
    '$500 to $1,500',
    '$1,500 to $3,000',
    '$3,000 or more',
    'Flexible / please advise',
  ];

  const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
  const toCents = (dollars) => Math.round((dollars + Number.EPSILON) * 100);
  const cleanText = (value, maximumLength) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, maximumLength);

  const parseWeight = (value) => {
    const raw = String(value ?? '').trim();
    if (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(raw)) return null;
    const weight = Number(raw);
    if (!isFiniteNumber(weight) || weight < MIN_WEIGHT_GRAMS || weight > MAX_WEIGHT_GRAMS) return null;
    return weight;
  };

  const parseKarat = (value) => {
    const karat = Number(value);
    return KARATS.includes(karat) ? karat : null;
  };

  const parseSpotUsd = (value) => {
    const spot = Number(value);
    const [minimum, maximum] = VALID_SPOT_RANGE_USD;
    return isFiniteNumber(spot) && spot >= minimum && spot <= maximum ? spot : null;
  };

  const calculateEstimate = ({ spotUsd, weightGrams, karat }) => {
    const spot = parseSpotUsd(spotUsd);
    const weight = parseWeight(weightGrams);
    const normalizedKarat = parseKarat(karat);
    if (spot === null || weight === null || normalizedKarat === null) return null;

    const marketDollars = (spot / TROY_OUNCE_GRAMS) * (normalizedKarat / 24) * weight;
    const cashDollars = marketDollars * CASH_FACTOR;
    const tradeDollars = marketDollars * TRADE_FACTOR;
    if (![marketDollars, cashDollars, tradeDollars].every(isFiniteNumber)) return null;

    const marketCents = toCents(marketDollars);
    const cashCents = toCents(cashDollars);
    const tradeCents = toCents(tradeDollars);
    return Object.freeze({
      spotUsd: spot,
      weightGrams: weight,
      karat: normalizedKarat,
      marketCents,
      cashCents,
      tradeCents,
      extraTradeValueCents: Math.max(tradeCents - cashCents, 0),
    });
  };

  const calculateContribution = ({ ringPriceCents, tradeCents }) => {
    const ringPrice = Number(ringPriceCents);
    const trade = Number(tradeCents);
    if (!Number.isSafeInteger(ringPrice) || ringPrice < 0 || !Number.isSafeInteger(trade) || trade < 0) return null;
    return Object.freeze({
      additionalPaymentCents: Math.max(ringPrice - trade, 0),
      unresolvedExcessCents: Math.max(trade - ringPrice, 0),
    });
  };

  const normalizeTimestamp = (value) => {
    const text = cleanText(value, 40);
    const milliseconds = Date.parse(text);
    return text && Number.isFinite(milliseconds) ? { text, milliseconds } : null;
  };

  const normalizeQuote = (quote, now) => {
    if (!quote || typeof quote !== 'object') return null;
    const spotUsd = parseSpotUsd(quote.spotUsd);
    const timestamp = normalizeTimestamp(quote.quoteTimestamp);
    const currency = cleanText(quote.currency, 3).toUpperCase();
    if (spotUsd === null || !timestamp || currency !== 'USD') return null;
    const age = now - timestamp.milliseconds;
    if (age < 0 || age > MAX_QUOTE_AGE_MS) return null;
    return {
      spotUsd,
      currency,
      source: cleanText(quote.source, 80) || 'Unknown',
      provider: cleanText(quote.provider, 40) || 'Unknown',
      status: quote.status === 'stale' || age > MAX_FRESH_AGE_MS ? 'stale' : 'fresh',
      quoteTimestamp: timestamp.text,
    };
  };

  const normalizeProductPath = (value) => {
    const path = cleanText(value, 500);
    if (!/^\/products\/[a-z0-9][a-z0-9-]*(?:\?variant=\d+)?$/i.test(path)) return '';
    return path;
  };

  const normalizeRing = (ring) => {
    if (!ring || typeof ring !== 'object') return null;
    const productId = cleanText(ring.productId, 32);
    const variantId = cleanText(ring.variantId, 32);
    const priceCents = Number(ring.priceCents);
    const currency = cleanText(ring.currency, 3).toUpperCase();
    const sku = cleanText(ring.sku, 80) || 'Unknown';
    if (!/^\d+$/.test(productId) || !/^\d+$/.test(variantId) || sku === '94721') return null;
    if (!Number.isSafeInteger(priceCents) || priceCents < 0 || !/^[A-Z]{3}$/.test(currency)) return null;
    const options = Array.isArray(ring.options)
      ? ring.options.slice(0, 6).map((value) => cleanText(value, 100)).filter(Boolean)
      : [];
    return {
      productId,
      variantId,
      sku,
      title: cleanText(ring.title, 180) || 'Unknown ring',
      options,
      priceCents,
      currency,
      url: normalizeProductPath(ring.url),
    };
  };

  const normalizeMode = (value) => (value === 'cash' ? 'cash' : 'trade');
  const resolveJourneyPath = ({ mode, requestedPath, hasRing }) => {
    if (normalizeMode(mode) === 'cash') return 'cash-evaluation';
    if (requestedPath === 'custom-ring-trade') return 'custom-ring-trade';
    if (requestedPath === 'selected-ring-trade' && hasRing) return 'selected-ring-trade';
    if (requestedPath === 'trade-consultation') return 'trade-consultation';
    return hasRing ? 'selected-ring-trade' : 'trade-consultation';
  };
  const normalizeAdditionalBudget = (value) => {
    const budget = cleanText(value, 80);
    return ADDITIONAL_BUDGETS.includes(budget) ? budget : '';
  };

  const sanitizeContext = (candidate, now = Date.now()) => {
    if (!candidate || typeof candidate !== 'object' || Number(candidate.version) !== VERSION) return null;
    const updated = normalizeTimestamp(candidate.updatedAt);
    if (!updated || now - updated.milliseconds < 0 || now - updated.milliseconds > MAX_CONTEXT_AGE_MS) return null;

    const weightGrams = parseWeight(candidate?.gold?.weightGrams);
    const karat = parseKarat(candidate?.gold?.karat);
    const quote = normalizeQuote(candidate.quote, now);
    const estimate = quote && weightGrams !== null && karat !== null
      ? calculateEstimate({ spotUsd: quote.spotUsd, weightGrams, karat })
      : null;
    const ring = normalizeRing(candidate.ring);
    const contribution = estimate && ring && ring.currency === quote.currency
      ? calculateContribution({ ringPriceCents: ring.priceCents, tradeCents: estimate.tradeCents })
      : null;
    const mode = normalizeMode(candidate.mode);

    return {
      version: VERSION,
      updatedAt: updated.text,
      sourcePath: '/pages/sell-your-gold',
      path: resolveJourneyPath({ mode, requestedPath: candidate.path, hasRing: Boolean(ring) }),
      mode,
      gold: {
        weightGrams,
        karat,
      },
      quote,
      estimate,
      ring,
      contribution,
      additionalBudget: normalizeAdditionalBudget(candidate.additionalBudget),
      needsRevalidation: !quote || Boolean(ring && ring.currency !== quote.currency),
    };
  };

  const createContext = (candidate, now = Date.now()) => sanitizeContext({
    ...candidate,
    version: VERSION,
    updatedAt: new Date(now).toISOString(),
    sourcePath: '/pages/sell-your-gold',
  }, now);

  const readContext = (storage, now = Date.now()) => {
    if (!storage || typeof storage.getItem !== 'function') return null;
    try {
      const raw = storage.getItem(STORAGE_KEY);
      return raw ? sanitizeContext(JSON.parse(raw), now) : null;
    } catch (_error) {
      return null;
    }
  };

  const writeContext = (storage, candidate, now = Date.now()) => {
    if (!storage || typeof storage.setItem !== 'function') return null;
    const context = createContext(candidate, now);
    if (!context) return null;
    const serializable = {
      version: context.version,
      updatedAt: context.updatedAt,
      sourcePath: context.sourcePath,
      path: context.path,
      mode: context.mode,
      gold: context.gold,
      quote: context.quote,
      ring: context.ring,
      additionalBudget: context.additionalBudget,
    };
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(serializable));
      return context;
    } catch (_error) {
      return null;
    }
  };

  return Object.freeze({
    VERSION,
    STORAGE_KEY,
    TROY_OUNCE_GRAMS,
    CASH_FACTOR,
    TRADE_FACTOR,
    MAX_WEIGHT_GRAMS,
    MIN_WEIGHT_GRAMS,
    MAX_FRESH_AGE_MS,
    MAX_QUOTE_AGE_MS,
    KARATS: Object.freeze(KARATS.slice()),
    ADDITIONAL_BUDGETS: Object.freeze(ADDITIONAL_BUDGETS.slice()),
    calculateEstimate,
    calculateContribution,
    resolveJourneyPath,
    sanitizeRing: normalizeRing,
    createContext,
    readContext,
    sanitizeContext,
    writeContext,
  });
});
